/*
 * BW SKIN STUDIO
 * REUSABLE MONTHLY OFFER AUTO-GIFT
 *
 * monthly-offer-a = qualifying Group A
 * monthly-offer-b = qualifying Group B
 *
 * SEPTEMBER GIFT:
 * IMAGE VITAL C Enzyme Masque 28g
 */

(() => {
  'use strict';

  const GIFT_VARIANT_ID = 51009844510887;

  /*
   * Special September rule:
   * Hydrating Repair Cream has multiple variants.
   * Only this full-size variant qualifies.
   */
  const RESTRICTED_GROUP_B_VARIANT = 51009766424743;

  const GIFT_PROPERTY = '_bw_monthly_gift';
  const GIFT_PROPERTY_VALUE = 'yes';

  let syncing = false;
  let timer = null;

  const root =
    window.Shopify &&
    window.Shopify.routes &&
    window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

  function url(path) {
    return root + path;
  }

  async function getCart() {
    const response = await fetch(url('cart.js'), {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Could not read Shopify cart');
    }

    return response.json();
  }

  async function getProduct(handle) {
    const response = await fetch(
      url('products/' + encodeURIComponent(handle) + '.js'),
      {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Could not read product: ' + handle);
    }

    return response.json();
  }

  function normaliseTags(tags) {
    if (Array.isArray(tags)) {
      return tags.map(tag =>
        String(tag).trim().toLowerCase()
      );
    }

    if (typeof tags === 'string') {
      return tags
        .split(',')
        .map(tag => tag.trim().toLowerCase())
        .filter(Boolean);
    }

    return [];
  }

  function isPromoGift(item) {
    return (
      Number(item.variant_id || item.id) === GIFT_VARIANT_ID &&
      item.properties &&
      item.properties[GIFT_PROPERTY] === GIFT_PROPERTY_VALUE
    );
  }

  async function qualifies(cart) {
    let hasA = false;
    let hasB = false;

    for (const item of cart.items) {
      if (isPromoGift(item)) {
        continue;
      }

      if (!item.handle) {
        continue;
      }

      const product = await getProduct(item.handle);
      const tags = normaliseTags(product.tags);

      /*
       * GROUP A
       */
      if (tags.includes('monthly-offer-a')) {
        hasA = true;
      }

      /*
       * GROUP B
       */
      if (tags.includes('monthly-offer-b')) {
        const productVariantIds = Array.isArray(product.variants)
          ? product.variants.map(v => Number(v.id))
          : [];

        /*
         * Is this the special Hydrating Repair Cream product?
         */
        const isRestrictedProduct =
          productVariantIds.includes(
            RESTRICTED_GROUP_B_VARIANT
          );

        if (isRestrictedProduct) {
          /*
           * Only the specified full-size variant qualifies.
           */
          if (
            Number(item.variant_id || item.id) ===
            RESTRICTED_GROUP_B_VARIANT
          ) {
            hasB = true;
          }
        } else {
          hasB = true;
        }
      }

      if (hasA && hasB) {
        return true;
      }
    }

    return false;
  }

  async function addGift() {
    console.log('[BW Monthly Gift] Adding gift');

    const response = await fetch(url('cart/add.js'), {
      method: 'POST',

      credentials: 'same-origin',

      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },

      body: JSON.stringify({
        items: [
          {
            id: GIFT_VARIANT_ID,
            quantity: 1,

            properties: {
              [GIFT_PROPERTY]: GIFT_PROPERTY_VALUE
            }
          }
        ]
      })
    });

    if (!response.ok) {
      const error = await response.text();

      throw new Error(
        'Gift could not be added: ' + error
      );
    }
  }

  async function changeGift(key, quantity) {
    const response = await fetch(url('cart/change.js'), {
      method: 'POST',

      credentials: 'same-origin',

      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },

      body: JSON.stringify({
        id: key,
        quantity: quantity
      })
    });

    if (!response.ok) {
      const error = await response.text();

      throw new Error(
        'Gift could not be updated: ' + error
      );
    }
  }

  async function syncGift() {
    if (syncing) {
      return;
    }

    syncing = true;

    try {
      const cart = await getCart();

      console.log(
        '[BW Monthly Gift] Checking cart',
        cart.items
      );

      const cartQualifies = await qualifies(cart);

      console.log(
        '[BW Monthly Gift] Qualifies:',
        cartQualifies
      );

      const promoGiftLines =
        cart.items.filter(isPromoGift);

      /*
       * QUALIFIES
       */
      if (cartQualifies) {
        if (promoGiftLines.length === 0) {
          await addGift();

          /*
           * Reload so Horizon displays the newly
           * server-rendered cart correctly.
           */
          window.location.reload();

          return;
        }

        /*
         * Make sure there is only ONE gift.
         */
        const gift = promoGiftLines[0];

        if (Number(gift.quantity) !== 1) {
          await changeGift(gift.key, 1);

          window.location.reload();

          return;
        }

        /*
         * Remove duplicate promotional gift lines.
         */
        if (promoGiftLines.length > 1) {
          for (
            let i = 1;
            i < promoGiftLines.length;
            i++
          ) {
            await changeGift(
              promoGiftLines[i].key,
              0
            );
          }

          window.location.reload();

          return;
        }
      }

      /*
       * DOES NOT QUALIFY
       *
       * Remove only the promotional gift.
       */
      if (
        !cartQualifies &&
        promoGiftLines.length > 0
      ) {
        for (const gift of promoGiftLines) {
          await changeGift(gift.key, 0);
        }

        window.location.reload();

        return;
      }
    } catch (error) {
      console.error(
        '[BW Monthly Gift] ERROR:',
        error
      );
    } finally {
      syncing = false;
    }
  }

  function scheduleSync() {
    clearTimeout(timer);

    timer = setTimeout(() => {
      syncGift();
    }, 500);
  }

  /*
   * CHECK EXISTING CART WHEN PAGE LOADS
   */
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      syncGift,
      { once: true }
    );
  } else {
    syncGift();
  }

  /*
   * CHECK AGAIN WHEN HORIZON CHANGES THE CART.
   */
  document.addEventListener(
    'cart:update',
    scheduleSync
  );

  document.addEventListener(
    'cart:refresh',
    scheduleSync
  );

  /*
   * Horizon replaces cart markup dynamically.
   * Watching the cart drawer gives us a reliable
   * fallback after add/remove/quantity changes.
   */
  const observer = new MutationObserver(
    mutations => {
      const relevant = mutations.some(
        mutation => {
          const target = mutation.target;

          if (!(target instanceof Element)) {
            return false;
          }

          return Boolean(
            target.closest(
              '#cart-drawer, cart-drawer-component, cart-items-component'
            )
          );
        }
      );

      if (relevant) {
        scheduleSync();
      }
    }
  );

  function startObserver() {
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  if (document.body) {
    startObserver();
  } else {
    document.addEventListener(
      'DOMContentLoaded',
      startObserver,
      { once: true }
    );
  }

  console.log(
    '[BW Monthly Gift] System loaded'
  );
})();